import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Box, IconButton, Popper, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import {
  buildTime,
  formatDisplayDateTime,
  getTomorrowStr,
  parseTime,
  toLocalDateStr,
} from "@/lib/reminders";

/**
 * The date+time picker itself — extracted from `ReminderEmailUpcoming` (My Campaigns'
 * Notifications modal) so Campaign Builder Step 2's inline reminder rows can use the exact same
 * control instead of a second calendar/time implementation. `ReminderEmailUpcoming` wraps this
 * with its own delete button; Step 2's row wraps it with none.
 */

const ITEM_HEIGHT = 30;
const VISIBLE_ITEMS = 7;
const HOURS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0"));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));
const AMPM_OPTIONS = ["AM", "PM"] as const;
const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

// ── Time Column ───────────────────────────────────────────────────────────────

function TimeColumn({
  values,
  selected,
  onSelect,
}: {
  values: readonly string[];
  selected: string;
  onSelect: (v: string) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  // Capture initial values so the effect has no reactive dependencies.
  // Re-running on `selected` changes would snap the viewport on every click,
  // preventing users from scrolling to earlier items.
  const initialSelected = useRef(selected);
  const initialValues = useRef(values);

  useEffect(() => {
    if (!ref.current) return;
    const idx = initialValues.current.indexOf(initialSelected.current);
    if (idx >= 0) ref.current.scrollTop = idx * ITEM_HEIGHT;
  }, []);

  return (
    <Box
      ref={ref}
      // Stop wheel events from bubbling to the Dialog and hijacking scroll.
      onWheel={(e) => e.stopPropagation()}
      sx={{
        height: VISIBLE_ITEMS * ITEM_HEIGHT,
        overflowY: "auto",
        "&::-webkit-scrollbar": { display: "none" },
        scrollbarWidth: "none",
      }}
    >
      {values.map((v) => {
        const active = v === selected;
        return (
          <Box
            key={v}
            onClick={() => onSelect(v)}
            sx={{
              height: ITEM_HEIGHT,
              width: 44,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 13,
              fontWeight: active ? 600 : 400,
              borderRadius: "4px",
              cursor: "pointer",
              bgcolor: active ? "var(--color-dark-dust)" : "transparent",
              color: active ? "white" : "var(--color-rodeo-black)",
              "&:hover": { bgcolor: active ? "var(--color-dark-dust)" : "var(--color-light-dust)" },
            }}
          >
            {v}
          </Box>
        );
      })}
    </Box>
  );
}

// ── Mini Calendar ─────────────────────────────────────────────────────────────

function MiniCalendar({
  value,
  onSelect,
  onClear,
  minDate,
  maxDate,
}: {
  value: string;
  onSelect: (d: string) => void;
  onClear: () => void;
  /** `YYYY-MM-DD`, inclusive — days before this are disabled (the campaign's send date, or the
   *  previous reminder's own day, whichever this row is bounded by). */
  minDate?: string;
  /** `YYYY-MM-DD`, EXCLUSIVE — this day and everything after it is disabled (the campaign's
   *  expiration day: a reminder must fire strictly before it). */
  maxDate?: string;
}) {
  const today = new Date();
  const tomorrowStr = getTomorrowStr();
  const seed = value ? new Date(`${value}T00:00:00`) : new Date(tomorrowStr);
  const [viewYear, setViewYear] = useState(seed.getFullYear());
  const [viewMonth, setViewMonth] = useState(seed.getMonth());

  const todayStr = toLocalDateStr(today);

  const label = new Date(viewYear, viewMonth)
    .toLocaleString("en-US", { month: "long", year: "numeric" })
    .toUpperCase();

  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const daysInPrev = new Date(viewYear, viewMonth, 0).getDate();

  type Cell = { day: number; dateStr: string; current: boolean };
  const cells: Cell[] = [];

  for (let i = firstDay - 1; i >= 0; i--) {
    const d = daysInPrev - i;
    const pm = viewMonth === 0 ? 12 : viewMonth;
    const py = viewMonth === 0 ? viewYear - 1 : viewYear;
    cells.push({
      day: d,
      dateStr: `${py}-${String(pm).padStart(2, "0")}-${String(d).padStart(2, "0")}`,
      current: false,
    });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({
      day: d,
      dateStr: `${viewYear}-${String(viewMonth + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`,
      current: true,
    });
  }
  const trailing = cells.length % 7 === 0 ? 0 : 7 - (cells.length % 7);
  for (let d = 1; d <= trailing; d++) {
    const nm = viewMonth === 11 ? 1 : viewMonth + 2;
    const ny = viewMonth === 11 ? viewYear + 1 : viewYear;
    cells.push({
      day: d,
      dateStr: `${ny}-${String(nm).padStart(2, "0")}-${String(d).padStart(2, "0")}`,
      current: false,
    });
  }

  function prev() {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else setViewMonth((m) => m - 1);
  }
  function next() {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else setViewMonth((m) => m + 1);
  }

  return (
    <Box sx={{ flex: 1, p: "12px 12px 10px 12px" }}>
      {/* Month nav */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
        <IconButton size="small" onClick={prev} sx={{ p: 0.25, color: "var(--color-graphite)" }}>
          <ChevronLeftIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <Typography
          sx={{
            flex: 1,
            textAlign: "center",
            fontSize: 12,
            fontWeight: 700,
            color: "var(--color-rodeo-black)",
            letterSpacing: "0.6px",
          }}
        >
          {label}
        </Typography>
        <IconButton size="small" onClick={next} sx={{ p: 0.25, color: "var(--color-graphite)" }}>
          <ChevronRightIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {/* Weekday headers */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
        {WEEKDAYS.map((d) => (
          <Typography
            key={d}
            sx={{
              textAlign: "center",
              fontSize: 11,
              color: "var(--color-graphite)",
              fontWeight: 600,
              py: "3px",
            }}
          >
            {d}
          </Typography>
        ))}
      </Box>

      {/* Day cells */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "1px" }}>
        {cells.map((cell) => {
          const sel = cell.dateStr === value;
          const isPast = cell.dateStr <= todayStr;
          const isBeforeMin = minDate !== undefined && cell.dateStr < minDate;
          const isOnOrAfterMax = maxDate !== undefined && cell.dateStr >= maxDate;
          const disabled = isPast || isBeforeMin || isOnOrAfterMax;
          return (
            <Box
              key={cell.dateStr}
              onClick={() => !disabled && onSelect(cell.dateStr)}
              sx={{
                textAlign: "center",
                py: "4px",
                borderRadius: "4px",
                cursor: disabled ? "default" : "pointer",
                fontSize: 12,
                fontWeight: 400,
                bgcolor: sel ? "var(--color-dark-dust)" : "transparent",
                color: sel
                  ? "white"
                  : disabled || !cell.current
                    ? "#ccc"
                    : "var(--color-rodeo-black)",
                "&:hover": {
                  bgcolor:
                    disabled || sel
                      ? sel
                        ? "var(--color-dark-dust)"
                        : "transparent"
                      : "var(--color-light-dust)",
                },
              }}
            >
              {cell.day}
            </Box>
          );
        })}
      </Box>

      {/* Clear / Tomorrow — desktop only; mobile footer lives in DateTimePanel */}
      <Box sx={{ display: { xs: "none", md: "flex" }, gap: 2, mt: 1.5 }}>
        <Typography
          onClick={onClear}
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.5px",
            textTransform: "uppercase",
            color: "var(--color-dark-dust)",
            cursor: "pointer",
            "&:hover": { opacity: 0.7 },
          }}
        >
          Clear
        </Typography>
        <Typography
          onClick={() => onSelect(tomorrowStr)}
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.5px",
            textTransform: "uppercase",
            color: "var(--color-dark-dust)",
            cursor: "pointer",
            "&:hover": { opacity: 0.7 },
          }}
        >
          Tomorrow
        </Typography>
      </Box>
    </Box>
  );
}

// ── Combined DateTime Panel ───────────────────────────────────────────────────

function DateTimePanel({
  date,
  h,
  m,
  ap,
  onDate,
  onH,
  onM,
  onAp,
  onClear,
  minDate,
  maxDate,
}: {
  date: string;
  h: string;
  m: string;
  ap: "AM" | "PM";
  onDate: (d: string) => void;
  onH: (v: string) => void;
  onM: (v: string) => void;
  onAp: (v: "AM" | "PM") => void;
  onClear: () => void;
  minDate?: string;
  maxDate?: string;
}) {
  const tomorrowStr = getTomorrowStr();

  const footerTypoSx = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.5px",
    textTransform: "uppercase" as const,
    color: "var(--color-dark-dust)",
    cursor: "pointer",
    "&:hover": { opacity: 0.7 },
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        width: "100%",
        border: "1px solid var(--color-smoke)",
        borderRadius: "var(--radius-btn, 6px)",
        overflow: "hidden",
        bgcolor: "background.paper",
        mt: "2px",
      }}
    >
      <MiniCalendar
        value={date}
        onSelect={onDate}
        onClear={onClear}
        minDate={minDate}
        maxDate={maxDate}
      />
      {/* Divider: horizontal on mobile, vertical on desktop */}
      <Box
        sx={{
          width: { xs: "100%", md: "1px" },
          height: { xs: "1px", md: "auto" },
          bgcolor: "var(--color-smoke)",
          flexShrink: 0,
        }}
      />
      {/* Time columns */}
      <Box sx={{ display: "flex", py: 1, px: { xs: 0, md: 0.75 }, alignItems: "flex-start" }}>
        <Box
          sx={{
            flex: { xs: 1, md: "initial" },
            display: "flex",
            justifyContent: { xs: "center", md: "flex-start" },
          }}
        >
          <TimeColumn values={HOURS} selected={h} onSelect={onH} />
        </Box>
        <Box
          sx={{
            width: "1px",
            bgcolor: "var(--color-smoke)",
            alignSelf: "stretch",
            mx: { xs: 0, md: "2px" },
          }}
        />
        <Box
          sx={{
            flex: { xs: 1, md: "initial" },
            display: "flex",
            justifyContent: { xs: "center", md: "flex-start" },
          }}
        >
          <TimeColumn values={MINUTES} selected={m} onSelect={onM} />
        </Box>
        <Box
          sx={{
            width: "1px",
            bgcolor: "var(--color-smoke)",
            alignSelf: "stretch",
            mx: { xs: 0, md: "2px" },
          }}
        />
        <Box
          sx={{
            flex: { xs: 1, md: "initial" },
            display: "flex",
            justifyContent: { xs: "center", md: "flex-start" },
          }}
        >
          <TimeColumn
            values={AMPM_OPTIONS}
            selected={ap}
            onSelect={(v) => onAp(v as "AM" | "PM")}
          />
        </Box>
      </Box>
      {/* Mobile-only footer — desktop footer lives inside MiniCalendar */}
      <Box sx={{ display: { xs: "flex", md: "none" }, gap: 2, px: "12px", pb: "10px" }}>
        <Typography onClick={onClear} sx={footerTypoSx}>
          Clear
        </Typography>
        <Typography onClick={() => onDate(tomorrowStr)} sx={footerTypoSx}>
          Tomorrow
        </Typography>
      </Box>
    </Box>
  );
}

// ── Public component ──────────────────────────────────────────────────────────

export interface ReminderDateTimePickerProps {
  /** `date` as `YYYY-MM-DD`, `time` as 24h `HH:mm` — empty strings mean "not set". */
  value: { date: string; time: string };
  onChange: (date: string, time: string) => void;
  disabled?: boolean;
  /** Reddens the trigger's border — this specific reminder is the one an inline validation
   *  message is pointing at. */
  error?: boolean;
  /** Inclusive lower bound (`YYYY-MM-DD`) — days before it are disabled in the calendar. */
  minDate?: string;
  /** Exclusive upper bound (`YYYY-MM-DD`) — this day and any after it are disabled. */
  maxDate?: string;
}

export default function ReminderDateTimePicker({
  value,
  onChange,
  disabled,
  error,
  minDate,
  maxDate,
}: ReminderDateTimePickerProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  /**
   * Read straight off the prop, never mirrored into state.
   *
   * The panel emits every change upward and both consumers — Step 2's GiftReminders and My
   * Campaigns' Notifications modal — echo it back synchronously, so a local copy could only ever
   * be a second source of truth. It was exactly that: seeded once at mount, it kept the 09:00
   * `parseTime("")` returns for a slot that has not loaded yet, and never picked up the real time
   * when the server's dates arrived. The date came from the prop and updated; the time did not.
   * A 3:00 PM reminder therefore rendered as 9:00 AM — and, because `emitChange` sent that stale
   * time along, was SAVED as 9:00 AM the moment the sender touched its date.
   *
   * The contract this relies on: a consumer must feed `value` back. One that does not will find
   * the control inert.
   */
  const { h, m, ap } = parseTime(value.time);

  useEffect(() => {
    if (!open) return;
    function handleMouseDown(e: MouseEvent) {
      const target = e.target as Node;
      // The panel is rendered through a Popper — a PORTAL to document.body, outside
      // `containerRef` in the DOM — so a click inside it must be checked separately, or every
      // click on the calendar/time columns would look like an outside click and close them.
      if (containerRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [open]);

  function emitChange(date: string, nh: string, nm: string, nap: "AM" | "PM") {
    onChange(date, buildTime(nh, nm, nap));
  }

  const displayValue = value.date ? formatDisplayDateTime(value.date, h, m, ap) : "";

  return (
    <Box
      ref={containerRef}
      sx={{ position: "relative", display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}
    >
      {/* Trigger */}
      <Box
        onClick={() => !disabled && setOpen((o) => !o)}
        sx={{
          minWidth: 0,
          position: "relative",
          height: 52,
          border: "1px solid",
          borderColor: error
            ? "var(--color-good-wine)"
            : open
              ? "var(--color-dark-dust)"
              : "var(--color-smoke)",
          borderRadius: "var(--radius-btn, 6px)",
          display: "flex",
          alignItems: "center",
          px: 1.5,
          cursor: disabled ? "default" : "pointer",
          bgcolor: disabled ? "var(--color-clear-dust)" : "background.paper",
          opacity: disabled ? 0.7 : 1,
          transition: "border-color 0.15s",
          userSelect: "none",
          boxSizing: "border-box",
        }}
      >
        {/* Floating "Date" label */}
        <Typography
          sx={{
            position: "absolute",
            top: displayValue ? "-9px" : "50%",
            left: "12px",
            transform: displayValue ? "none" : "translateY(-50%)",
            fontSize: displayValue ? 11 : 15,
            color: open ? "var(--color-dark-dust)" : "var(--color-graphite)",
            bgcolor: "background.paper",
            px: 0.5,
            lineHeight: 1,
            transition: "all 0.15s",
            pointerEvents: "none",
          }}
        >
          Date
        </Typography>
        {displayValue && (
          <Typography
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: { xs: 12, sm: 15 },
              color: "var(--color-rodeo-black)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {displayValue}
          </Typography>
        )}
        {!displayValue && <Box sx={{ flex: 1 }} />}
        {!disabled && (
          <KeyboardArrowDownIcon
            sx={{
              fontSize: 20,
              color: "var(--color-graphite)",
              flexShrink: 0,
              transform: open ? "rotate(180deg)" : "none",
              transition: "transform 0.15s",
            }}
          />
        )}
      </Box>

      {/* Picker panel — a Popper, so it PORTALS to document.body instead of rendering as a normal
          child. Campaign Builder Step 2 nests this trigger inside an accordion whose expand/
          collapse animation relies on `overflow: hidden` on an ancestor — a plain absolutely-
          positioned child is still clipped by that ancestor's box no matter its z-index, which is
          exactly what cut the panel off for any reminder past the first row (nothing above it
          left enough room before the clip). A Popper isn't inside that box at all. */}
      <Popper
        open={open && !disabled}
        anchorEl={containerRef.current}
        placement="bottom-end"
        style={{ zIndex: 1300 }}
      >
        <Box
          ref={panelRef}
          sx={{
            mt: "4px",
            width: 380,
            maxWidth: "calc(100vw - 32px)",
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            borderRadius: "var(--radius-btn, 6px)",
          }}
        >
          <DateTimePanel
            date={value.date}
            h={h}
            m={m}
            ap={ap}
            onDate={(d) => emitChange(d, h, m, ap)}
            onH={(v) => emitChange(value.date, v, m, ap)}
            onM={(v) => emitChange(value.date, h, v, ap)}
            onAp={(v) => emitChange(value.date, h, m, v)}
            onClear={() => onChange("", "")}
            minDate={minDate}
            maxDate={maxDate}
          />
        </Box>
      </Popper>
    </Box>
  );
}
